import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { compose } from 'recompose'
import { connect as connectStaticText } from 'components/StaticText/connect'
import { all as allPromise } from 'bluebird'
import ImmutablePropTypes from 'react-immutable-proptypes'
import _get from 'lodash/get'
import _once from 'lodash/once'
import _isFunction from 'lodash/isFunction'
import _join from 'lodash/join'
import { getBoundActions } from 'actions'
import { get as getConfig } from 'config'
import { connect as connectRedux } from 'react-redux'
import { Map } from 'immutable'
import ReactPlayerLoader from '@brightcove/react-player-loader'
import {
  getVideoOpts,
  getMediaLangParams,
  setupVideoPlayer,
} from 'video-js'
import VideoPlayerSpinner from 'components/VideoPlayerSpinner'
import youbora from 'youboralib'
import 'youbora-adapter-videojs'
import NpawPlugin from 'npaw-plugin-nwf'
import NpawPluginAdapters from 'npaw-plugin-adapters'
import { getAuthJwt } from 'services/auth'
import IconV2, { ICON_TYPES } from 'components/Icon.v2'
import Button, { TYPES } from 'components/Button'
import {
  VIDEO_QUALIFIED_VIEW_GA4_EVENT,
  VIDEO_ENGAGED_VIEW_GA4_EVENT,
  VIDEO_COMPLETED_GA4_EVENT,
} from 'services/event-tracking'

export const BASIC_CONTROL_OPTIONS_CLOSE = 'close'
export const BASIC_CONTROL_OPTIONS_VOLUME = 'volume'
export const BASIC_CONTROL_OPTIONS_VOLUME_LABEL = 'volume-label'
export const BASIC_CONTROL_OPTIONS_PLAYBACK = 'playback'
export const BASIC_CONTROL_OPTIONS_PLAYBACK_LABEL = 'playback-label'
export const BASIC_CONTROLS = 'basic'
export const DEFAULT_CONTROLS = 'default'
const KEY_SPACEBAR = 32
const config = getConfig()
const USE_NPAW = _get(config, 'useNpaw')

/**
 * Initialize video JS plugins
 * @param {Object} options The options
 * @param {Object} options.enableVideoAnalytics When true enable video analytics
 * i.e. SMF video-js plugin
 */
async function initVideoJsPlugins (options = {}) {
  const { enableVideoAnalytics } = options
  const [SMFAnalyticsPlugin, SeekButtonsPlugin, KeyboardPlaybackTogglePlugin] = await allPromise([
    enableVideoAnalytics && import('video-js/smf-analytics'),
    import('video-js/seek-buttons'),
    import('video-js/keyboard-playback-toggle'),
  ])
  const registeredPlugins = global.videojs.getPlugins ? global.videojs.getPlugins() : {}
  if (
    SMFAnalyticsPlugin
    && registeredPlugins[SMFAnalyticsPlugin.PLUGIN_NAME] === undefined
  ) {
    SMFAnalyticsPlugin.register(global.videojs)
  }
  if (
    SeekButtonsPlugin
    && registeredPlugins[SeekButtonsPlugin.PLUGIN_NAME] === undefined
  ) {
    SeekButtonsPlugin.register(global.videojs)
  }
  if (
    KeyboardPlaybackTogglePlugin
    && registeredPlugins[KeyboardPlaybackTogglePlugin.PLUGIN_NAME] === undefined
  ) {
    KeyboardPlaybackTogglePlugin.register(global.videojs)
  }
}

class DropInVideoPlayer extends Component {
  constructor (props) {
    super(props)
    const { mediaWasFetched, playerRenderDelayMilliseconds } = props
    this.state = {
      mediaWasFetched,
      isMuted: props.autoplay || false,
      isPlaying: props.autoplay || false,
      videoReady: false,
      renderPlayer: false,
    }
    this.qualified = false
    this.renderPlayerTimeout = setTimeout(() => {
      this.renderPlayerTimeout = undefined
      // Player already rendered
      if (this.state.renderPlayer) {
        return
      }
      this.setState({ renderPlayer: true })
    }, playerRenderDelayMilliseconds > 0 ? playerRenderDelayMilliseconds : 0)
  }

  componentDidMount () {
    const { props, state } = this
    const { mediaWasFetched } = state
    const { playerSrc } = props
    if (!playerSrc && !mediaWasFetched) {
      this.getMedia()
    }
  }

  componentDidUpdate (prevProps) {
    const { auth = Map() } = this.props
    const { prevAuth = Map() } = prevProps
    if (auth !== prevAuth && this._player && this._player.setState) {
      // Keep the video player state in sync
      try {
        this._player.setState({ jwt: getAuthJwt(auth) })
      } catch (_) {
        // If this fails catch the error to prevent the render from breaking
      }
    }
  }

  componentWillUnmount () {
    if (this.renderPlayerTimeout) {
      clearTimeout(this.renderPlayerTimeout)
      this.renderPlayerTimeout = undefined
    }
    this.disposePlayer()
  }

  onClickClose = (e) => {
    e.preventDefault()
    const { props } = this
    const { onClosePlayer } = props
    if (onClosePlayer) {
      onClosePlayer()
    }
  }

  onTogglePlayButtonClick = async (evt) => {
    const { _player, props } = this
    const { optionalPlaybackToggleFunction } = props

    if (_player) {
      const { isPlaying } = this.state
      evt.preventDefault()
      try {
        if (isPlaying) {
          await _player.pause()
        } else {
          await _player.play()
        }
        this.setState({ isPlaying: !isPlaying })
        if (_isFunction(optionalPlaybackToggleFunction)) {
          optionalPlaybackToggleFunction(!isPlaying)
        }
      } catch (e) {
        // Do nothing player call failed
      }
    }
  }

  onToggleMuteButtonClick = (evt) => {
    const { props, state } = this
    const { optionalMuteToggleFunction } = props
    const { isMuted } = state
    evt.preventDefault()
    try {
      if (isMuted) {
        this._player.muted(false)
      } else {
        this._player.muted(true)
      }
      this.setState({ isMuted: !isMuted })
      if (_isFunction(optionalMuteToggleFunction)) {
        optionalMuteToggleFunction(!isMuted)
      }
    } catch (e) {
      // Do nothing player call failed
    }
  }

  onPlayerSuccess = async (data) => {
    const {
      defaultTextTracks,
      media,
      playerSrc,
      startAt,
      textTracks,
      type,
      user,
      userVideo,
      enableVideoAnalytics,
      onPlayerSuccess,
      withoutControls,
    } = this.props

    await initVideoJsPlugins({
      enableVideoAnalytics,
    })

    const { ref: dataPlayer } = data
    const component = this
    dataPlayer.ready(function onPlayerReady () {
      const player = setupVideoPlayer({
        // Use the player instance from the ready function
        player: this,
        media,
        type,
        user,
        userVideo,
        location: global.location,
        src: playerSrc,
        text: textTracks,
        startAt,
        isLiveVideo: defaultTextTracks,
      })

      if (!player) {
        return
      }

      component.setVideoPlayer(player)
      component.configurePlayerPlugins(player)
      component.registerPlayerEventHandlers(player)
      component.setState({ videoReady: true })

      // Call props onPlayerSuccess if it is a function
      if (_isFunction(onPlayerSuccess)) {
        onPlayerSuccess(data)
      }
    })

    if (withoutControls) {
      dataPlayer.player().controlBar.hide()
    }
  }

  // ONLY CALL IN COMPONENT DID UPDATE IF WE HAVE MEDIA ID
  getMedia () {
    const {
      props,
      state,
    } = this

    const {
      auth,
      user,
      mediaId,
      getMediaData,
    } = props

    if (state.mediaWasFetched) return
    if (mediaId) {
      // TODO: improve mediaWasFetched flag to account for errors from getMediaData
      this.setState({ mediaWasFetched: true })
      getMediaData({ id: mediaId, auth, user })
    }
  }

  setVideoPlayer (player) {
    this._player = player
  }

  getClassName () {
    const { props } = this
    const { styled, className } = props
    const cls = ['drop-in-video-player']
    if (styled) {
      cls.push('drop-in-video-player__styled')
    }
    if (className) {
      cls.push(className)
    }
    return _join(cls, ' ')
  }

  disposePlayer = () => {
    if (!this._player) {
      return
    }
    // React player loader component will dispose player
    this._player = undefined
  }

  /**
   * Configure the video-js player plugins
   * @param {Object} player Brightcove video player
   */
  configurePlayerPlugins = (player) => {
    const { props } = this
    const {
      auth,
      videoId,
      mediaId,
      media,
      user,
      /** When true enable youbora data collection */
      enableYoubora,
      /** When true enable video analytics i.e. SMF video-js plugin */
      enableVideoAnalytics,
      videoAnalyticsPlayerType,
      mediaDuration,
      mediaLanguage,
      ga4EventData,
      setDefaultGa4Event,
    } = props
    const trueMediaId = mediaId || media.get('id')
    const duration = Map.isMap(media) ? media.getIn(['data', 'duration']) : 0
    const uid = auth.get('uid', null)
    const jwt = auth.get('jwt', null)
    const anonymousUuid = user.getIn(['data', 'anonymousUuid'])
    const mediaLang = media.getIn(['data', 'mediaLang'])
    if (enableYoubora && !USE_NPAW) {
      this.configureYouboralugin(player)
    } else if (enableYoubora && USE_NPAW) {
      this.configureNpawPlugin(player)
    }
    if (enableVideoAnalytics) {
      player.SmfAnalytics({
        uid,
        jwt,
        anonymousUuid,
        duration: mediaDuration || duration,
        mediaId: trueMediaId,
        mediaLang: mediaLanguage || mediaLang,
        videoId: videoId || trueMediaId,
        playerType: videoAnalyticsPlayerType,
        onQualifiedViewEvent: ((videoAnalyticsId) => {
          // GA4 event
          if (ga4EventData) {
            setDefaultGa4Event(VIDEO_QUALIFIED_VIEW_GA4_EVENT.mergeDeep({
              ...ga4EventData,
              video_play_event_id: videoAnalyticsId,
            }))
          }
        }),
        onWatchCompletedEvent: ((videoAnalyticsId) => {
          // GA4 event
          if (ga4EventData) {
            setDefaultGa4Event(VIDEO_COMPLETED_GA4_EVENT.mergeDeep({
              ...ga4EventData,
              video_play_event_id: videoAnalyticsId,
            }))
          }
        }),
        onVideoEngagedEvent: ((videoAnalyticsId) => {
          // GA4 event
          if (ga4EventData) {
            setDefaultGa4Event(VIDEO_ENGAGED_VIEW_GA4_EVENT.mergeDeep({
              ...ga4EventData,
              video_play_event_id: videoAnalyticsId,
            }))
          }
        }),
      })
    }

    if (props.controls === DEFAULT_CONTROLS) {
      player.KeyboardPlaybackToggle({ keys: [KEY_SPACEBAR] })
      player.SeekButtons({ forward: 30, back: 10 })
    }
  }

  static configureNpawplugin (player) {
    const { props } = this
    const {
      app,
      auth,
      playerSrc,
      media,
      mediaId,
      title = null,
      user,
    } = props
    const uid = auth.get('uid', null)
    const trueMediaId = mediaId || media.get('id')
    const mediaOptions = getMediaLangParams(user, media, null)
    const youboraTitle = title || 'Instructor Video'
    const mediaUrl = playerSrc || mediaOptions.url
    const npawPlugin = new NpawPlugin(_get(config, 'youboraAccountCode', 'unknown'))
    npawPlugin.registerAdapterFromClass(player, NpawPluginAdapters.video.Videojs)
    npawPlugin.setVideoOptions({
      'parse.hls': true,
      httpSecure: true,
      enabled: true,
      username: uid,
      'content.metadata': {
        content_id: trueMediaId,
      },
      'content.title': youboraTitle,
      'content.isLive': false,
      'content.resource': mediaUrl,
      'extraparam.1': app.get('name', 'web-app'),
      'extraparam.2': app.get('version', '0.0.0'),
      'extraparam.3': _get(config, ['brightcove', 'player'], 'unknown'),
      'extraparam.4': 'DropInVideoPlayer',
      'extraparam.5': Map.isMap(auth) && auth.get('jwt') ? 'logged-in' : 'anonymous',
      'extraparam.6': 'Unknown',
      'extraparam.7': 'unknown',
    })
  }

  static configureYouboraplugin (player) {
    const { props } = this
    const {
      app,
      auth,
      playerSrc,
      media,
      mediaId,
      title = null,
      user,
    } = props
    const uid = auth.get('uid', null)
    const trueMediaId = mediaId || media.get('id')
    const mediaOptions = getMediaLangParams(user, media, null)
    const youboraTitle = title || 'Instructor Video'
    const mediaUrl = playerSrc || mediaOptions.url
    const youboraPlugin = new youbora.Plugin({ accountCode: _get(config, 'youboraAccountCode', 'unknown') })
    youboraPlugin.setAdapter(new youbora.adapters.Videojs(player))
    youboraPlugin.setOptions({
      'parse.hls': true,
      httpSecure: true,
      enabled: true,
      username: uid,
      'content.metadata': {
        content_id: trueMediaId,
      },
      'content.title': youboraTitle,
      'content.isLive': false,
      'content.resource': mediaUrl,
      'extraparam.1': app.get('name', 'web-app'),
      'extraparam.2': app.get('version', '0.0.0'),
      'extraparam.3': _get(config, ['brightcove', 'player'], 'unknown'),
      'extraparam.4': 'DropInVideoPlayer',
      'extraparam.5': Map.isMap(auth) && auth.get('jwt') ? 'logged-in' : 'anonymous',
      'extraparam.6': 'Unknown',
      'extraparam.7': 'unknown',
    })
  }

  registerPlayerEventHandlers = (player) => {
    const { props } = this
    const {
      autoplay,
      handleVideoEnd,
      media,
      qualifyViewsAction,
      setVideoPlayerEnded,
      volume,
      handleVideoPaused,
      handleVideoPlay,
      starOnCanPlay,
    } = props
    const onceReloadLocation = _once(() => {
      global.location.reload()
    })
    if (starOnCanPlay) {
      player.on('canplay', async function onCanPlay () {
        try {
          await this.play()
        } catch (e) {
          // Do nothing the brower might not allow playback
        }
      })
    }
    try {
      player.on('timeupdate', function onTimeUpdate () {
        if (this.currentTime() < 15.0) {
          return
        }
        if (qualifyViewsAction && !this.qualified) {
          qualifyViewsAction()
        }
        this.qualified = true
      })
    } catch (e) {
      // Do nothing
    }
    try {
      player.on('timeupdate', () => {
        if (!Map.isMap(media) || !media.getIn(['data', 'expiresTime'])) {
          return
        }
        // Check if the current media has expired.
        const date = new Date()
        const currentTime = date.getTime()
        // Once it has expired just reload the page.
        if (currentTime > media.getIn(['data', 'expiresTime'])) {
          onceReloadLocation()
        }
      })
    } catch (e) {
      // Do nothing
    }
    try {
      player.on('ended', () => {
        setVideoPlayerEnded(true)
        if (_isFunction(handleVideoEnd)) {
          try {
            handleVideoEnd()
          } catch (e) {
            // Do nothing
          }
        }
      })
    } catch (e) {
      // Do nothing
    }
    try {
      player.on('ended', async function onEnded () {
        // Edge Bug - Prevents Player Timeout Error
        try {
          if (!this.loop()) {
            return
          }
          if (this.currentTime() > 0) {
            this.currentTime(0)
          }
          await this.play()
        } catch (e) {
          // Do nothing the brower might not allow playback
        }
      })
    } catch (e) {
      // Do nothing
    }
    // kind of bad, but always true that if we are attempting to autoplay,
    // the player MUST be muted
    if (!volume) {
      try {
        player.muted(autoplay)
      } catch (_) {
        // Do nothing
      }
    }
    try {
      // Autoplay
      if (autoplay) {
        player.on('loadedmetadata', async function onLoadedMetaData () {
          try {
            await this.play()
          } catch (_) {
            // Browser may not allow play
          }
        })
      }
    } catch (e) {
      // Do nothing
    }
    try {
      player.on('pause', () => {
        if (_isFunction(handleVideoPaused)) {
          try {
            handleVideoPaused(player)
          } catch (e) {
            // Do nothing
          }
        }
      })
    } catch (e) {
      // Do nothing
    }
    try {
      player.on('play', () => {
        if (_isFunction(handleVideoPlay)) {
          try {
            handleVideoPlay()
          } catch (e) {
            // Do nothing
          }
        }
      })
    } catch (e) {
      // Do nothing
    }
  }

  canRenderVideo () {
    const { props } = this
    const { media, mediaId, playerSrc } = props
    const processing = media.get('processing')
    const mediaReady = mediaId === media.get('id')
    if (!playerSrc && (processing || !mediaReady)) {
      return false
    }
    return true
  }

  renderBasicControls () {
    const { state, props } = this
    const {
      staticText,
      customPlaybackPlayingLabel,
      customPlaybackPausedLabel,
      customVolumeOnLabel,
      customVolumeOffLabel,
    } = props
    const visibleControls = _get(props, 'controls.visibleControls', [])
    const renderCloseControl = visibleControls.indexOf(BASIC_CONTROL_OPTIONS_CLOSE) > -1
    const renderVolumeToggle = visibleControls.indexOf(BASIC_CONTROL_OPTIONS_VOLUME) > -1
    const renderVolumeLabel = visibleControls.indexOf(BASIC_CONTROL_OPTIONS_VOLUME_LABEL) > -1
    const renderPlaybackToggle = visibleControls.indexOf(BASIC_CONTROL_OPTIONS_PLAYBACK) > -1
    const renderPlaybackLabel = visibleControls.indexOf(BASIC_CONTROL_OPTIONS_PLAYBACK_LABEL) > -1
    const volumeIconType = state.isMuted ? ICON_TYPES.VOLUME_NULL : ICON_TYPES.VOLUME_FULL
    const volumeLabel = state.isMuted
      ? customVolumeOffLabel || staticText.getIn(['data', 'audioOff'])
      : customVolumeOnLabel || staticText.getIn(['data', 'audioOn'])
    const playToggleIconType = state.isPlaying ? ICON_TYPES.PAUSE : ICON_TYPES.PLAY
    const playbackLabel = state.isPlaying
      ? customPlaybackPlayingLabel || staticText.getIn(['data', 'pause'])
      : customPlaybackPausedLabel || staticText.getIn(['data', 'resume'])

    return (
      <div className="drop-in-video-player__button-group-controls">
        {
          renderPlaybackToggle ? (
            <Button
              type={TYPES.LINK}
              onClick={this.onTogglePlayButtonClick}
              className="drop-in-video-player__toggle-play-button"
            >
              <IconV2 type={playToggleIconType} />
              {renderPlaybackLabel ? (<span className="drop-in-video-player__toggle-playback-label">&nbsp; {playbackLabel} </span>) : ''}
            </Button>
          ) : null
        }
        {
          renderVolumeToggle
            ? (
              <Button
                type={TYPES.LINK}
                onClick={this.onToggleMuteButtonClick}
                className="drop-in-video-player__toggle-mute-button"
              >
                <IconV2 type={volumeIconType} />
                {renderVolumeLabel ? (<span className="drop-in-video-player__toggle-mute-label">&nbsp; {volumeLabel} </span>) : ''}
              </Button>
            )
            : null
        }
        {
          renderCloseControl ? (
            <Button type={TYPES.LINK} onClick={this.onClickClose}>
              <IconV2 type={ICON_TYPES.CLOSE} />
            </Button>
          ) : null
        }
      </div>
    )
  }

  render () {
    const { props, state } = this
    const { videoReady, renderPlayer } = state
    let { accountId, playerId } = props
    if (!videoReady && !this.canRenderVideo()) {
      return (
        <div className={this.getClassName()}>
          <VideoPlayerSpinner video={props.video} />
        </div>
      )
    }

    const isBasicControls = _get(props, 'controls.type') === BASIC_CONTROLS
    // If either a videoId or playerId are provided don't use config
    // otherwise use BC config
    if (!accountId && !playerId) {
      accountId = _get(config, 'brightcove.account')
      playerId = _get(config, 'brightcove.player')
    }
    const {
      user, loop, poster, playsinline = false,
    } = props
    const vjsOpts = getVideoOpts({
      user,
      poster: poster || '',
      loop,
      isLive: false,
      playsinline,
    })
    return (
      <div className={this.getClassName()}>
        <div className="drop-in-video-player__container video-player">
          {
            renderPlayer && <ReactPlayerLoader
              onSuccess={this.onPlayerSuccess}
              accountId={accountId}
              playerId={playerId}
              options={vjsOpts}
            />
          }
          {
            isBasicControls
              ? this.renderBasicControls()
              : null
          }
        </div>
      </div>
    )
  }
}

DropInVideoPlayer.propTypes = {
  staticText: ImmutablePropTypes.map.isRequired,
  accountId: PropTypes.number,
  playerId: PropTypes.number,
  videoId: PropTypes.number,
  app: ImmutablePropTypes.map.isRequired,
  media: ImmutablePropTypes.map.isRequired,
  mediaId: PropTypes.number,
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  videoPlayer: ImmutablePropTypes.map.isRequired,
  controls: PropTypes.shape({
    type: PropTypes.oneOf([DEFAULT_CONTROLS, BASIC_CONTROLS]).isRequired,
    visibleControls: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  loop: PropTypes.bool,
  autoplay: PropTypes.bool,
  starOnCanPlay: PropTypes.bool,
  poster: PropTypes.string,
  playsinline: PropTypes.bool,
  withoutControls: PropTypes.bool,
  handleVideoEnd: PropTypes.func,
  className: PropTypes.string,
  startAt: PropTypes.number,
  onPlayerSuccess: PropTypes.func,
  onClosePlayer: PropTypes.func,
  handleVideoPaused: PropTypes.func,
  handleVideoPlay: PropTypes.func,
  customVolumeOnLabel: PropTypes.string,
  customVolumeOffLabel: PropTypes.string,
  customPlaybackPlayingLabel: PropTypes.string,
  customPlaybackPausedLabel: PropTypes.string,
  optionalMuteToggleFunction: PropTypes.func,
  optionalPlaybackToggleFunction: PropTypes.func,
  /**
   * When true enable video analytics data collection i.e. SMF video-js plugin and
   */
  enableVideoAnalytics: PropTypes.bool,
  videoAnalyticsPlayerType: PropTypes.string,
  /**
   * When true enable youbora data collection
   */
  enableYoubora: PropTypes.bool,
  /** The time in milliseconds to delay the player render */
  playerRenderDelayMilliseconds: PropTypes.number,
  /** media data for shared videos VA */
  mediaDuration: PropTypes.number,
  mediaLanguage: PropTypes.string,
  /** The ga4 event data object */
  ga4EventData: PropTypes.object,
}

DropInVideoPlayer.defaultProps = {
  enableVideoAnalytics: false,
  enableYoubora: false,
  playerRenderDelayMilliseconds: 250,
  withoutControls: false,
}

export default compose(
  connectRedux(
    (state) => {
      return {
        app: state.app,
        auth: state.auth,
        user: state.user,
        videoPlayer: state.videoPlayer,
        video: state.video,
        media: state.media,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        getMediaData: actions.media.getMediaData,
        setVideoPlayerEnded: actions.videoPlayer.setVideoPlayerEnded,
        setDefaultGa4Event: actions.eventTracking.setDefaultGa4Event,
      }
    },
  ),
  connectStaticText({ storeKey: 'dropInVideoPlayer' }),
)(DropInVideoPlayer)
